import { create } from 'zustand';
import { persist, devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import {
  SLOW_MAX_KW,
  FAST_MIN_KW,
  FAST_MAX_KW,
  ULTRA_FAST_MIN_KW,
} from '@/utils/constants';
import type {
  ChargeSpeedType,
  ChargePointFilterParams,
} from '../types/filter.types';

interface FilterState {
  // Availability filters
  showOnlyAvailable: boolean; // When true, show only available charge points
  showAutoCharge: boolean;

  // Charge speed filters
  showSlow: boolean;
  showFast: boolean;
  showUltraFast: boolean;

  // Operator filters
  selectedOperators: number[]; // Deprecated - use selectedTeamIds and selectedOperatorIds
  selectedOperatorSuggestionIds: number[]; // IDs of selected operator suggestions from map_suggestions
  selectedTeamIds: number[]; // Actual team IDs to send to API
  selectedOperatorIds: number[]; // Actual operator IDs to send to API
  selectedPromotionIds: number[]; // Promotion IDs to filter

  // Scope pinned by the host page via the embed URL (operator_scope=restrict).
  // Not a user choice, so resetFilters returns to it instead of clearing it:
  // a restricted embed hides the operator filter, so a reset that dropped the
  // scope would show every charge point with no way back.
  urlScope: {
    operatorSuggestionIds: number[];
    operatorIds: number[];
    teamIds: number[];
    promotionIds: number[];
  };

  // Numeric filters
  maxPricePerKwh: number;
  minStabilityScore: number;

  // UI state
  isFilterPanelOpen: boolean;
  isBestPriceModalOpen: boolean;
  expandedSections: {
    availability: boolean;
    autoCharge: boolean;
    chargeSpeed: boolean;
    operators: boolean;
    price: boolean;
    stability: boolean;
    help: boolean;
  };
  controlsEnabled: boolean;
  showOperatorFilter: boolean; // Whether to show the operators filter section (default: true)
  showAutoChargeFilter: boolean; // Whether to show the auto charge filter section (default: false)
  isLocationExpanded: boolean; // Whether the LocationBlock card is expanded (shows pin toggle + address/coords)
  showAnchorPoint: boolean; // Whether to show the center location marker (default: true)
  showBestPriceButton: boolean; // Whether to show the "Find best price" button (default: true)
  showLocationDetails: boolean; // Whether to show the filter sections block (default: true, set false via location_details=false)
  showPriceFilter: boolean; // Whether to show the price-per-kWh filter section (default: true, set false via filter_price_ui=0)
  showShareButton: boolean; // Whether to show the Share button (default: true, set false via share_ui=0)
  anchorPointVisible: boolean; // User's explicit choice to show the center/anchor pin on the map (default: false, not persisted, not round-tripped)
  markerStyle: 'default' | 'operator' | 'operator_neutral'; // Marker style: default (black center), operator (primary color center), or operator_neutral (beige center, no badge border)

  // Computed
  hasActiveFilters: () => boolean;
  getApiFilters: () => ChargePointFilterParams;

  // Actions
  setShowOnlyAvailable: (value: boolean) => void;
  setShowAutoCharge: (value: boolean) => void;
  toggleChargeSpeed: (speed: ChargeSpeedType) => void;
  toggleOperator: (operatorId: number) => void; // Deprecated
  toggleOperatorSuggestion: (operator: {
    id: number;
    teamIds: number[];
    operatorIds: number[];
  }) => void;
  setOperatorIds: (ids: number[]) => void;
  setOperatorSuggestions: (
    operators: Array<{ id: number; teamIds: number[]; operatorIds: number[] }>
  ) => void;
  setMaxPrice: (price: number) => void;
  setMinStability: (score: number) => void;
  resetFilters: () => void;
  setUrlScope: (scope: Partial<FilterState['urlScope']>) => void;
  toggleFilterPanel: () => void;
  toggleBestPriceModal: () => void;
  setBestPriceModalOpen: (open: boolean) => void;
  toggleSection: (section: keyof FilterState['expandedSections']) => void;
  setControlsEnabled: (enabled: boolean) => void;
  setLocationExpanded: (expanded: boolean) => void;
  setShowAnchorPoint: (show: boolean) => void;
  setShowLocationDetails: (show: boolean) => void;
  setShowPriceFilter: (show: boolean) => void;
  setShowShareButton: (show: boolean) => void;
  setAnchorPointVisible: (visible: boolean) => void;
  setMarkerStyle: (style: 'default' | 'operator' | 'operator_neutral') => void;
}

/** Order-insensitive comparison of two id lists. */
const sameIds = (a: number[], b: number[]): boolean =>
  a.length === b.length && a.every((id) => b.includes(id));

export const useFilterStore = create<FilterState>()(
  devtools(
    persist(
      immer((set, get) => ({
        // Initial state
        showOnlyAvailable: false, // Default: show all charge points (available + busy + queue + passive)
        showAutoCharge: false,

        showSlow: true,
        showFast: true,
        showUltraFast: true,

        selectedOperators: [],
        selectedOperatorSuggestionIds: [],
        selectedTeamIds: [],
        selectedOperatorIds: [],

        selectedPromotionIds: [],

        urlScope: {
          operatorSuggestionIds: [],
          operatorIds: [],
          teamIds: [],
          promotionIds: [],
        },

        maxPricePerKwh: 0,
        minStabilityScore: 0,

        isFilterPanelOpen: false, // Default closed - URL param filter_ui=1 opens it
        isBestPriceModalOpen: false, // Default to closed
        expandedSections: {
          availability: true,
          autoCharge: true,
          chargeSpeed: true,
          operators: false,
          price: false,
          stability: false,
          help: false,
        },
        controlsEnabled: true,
        showOperatorFilter: true,
        showAutoChargeFilter: false,
        isLocationExpanded: true, // Location card expanded by default; user can minimize it
        showAnchorPoint: true, // Show center location marker by default
        showBestPriceButton: true, // Show "Find best price" button by default
        showLocationDetails: true, // Show filter sections block by default
        showPriceFilter: true, // Show price-per-kWh filter section by default
        showShareButton: true, // Show Share button by default
        anchorPointVisible: false, // Anchor pin hidden until the user explicitly toggles it on
        markerStyle: 'default', // Default marker style with black center

        // Check if any filters are active (non-default state).
        // "Default" means the URL-pinned scope, not an empty scope - otherwise
        // a restricted embed shows a Reset link that resets nothing.
        hasActiveFilters: () => {
          const state = get();
          return (
            state.showOnlyAvailable ||
            state.showAutoCharge ||
            state.maxPricePerKwh > 0 ||
            !sameIds(
              state.selectedOperatorSuggestionIds,
              state.urlScope.operatorSuggestionIds
            ) ||
            !sameIds(state.selectedOperatorIds, state.urlScope.operatorIds) ||
            !sameIds(state.selectedTeamIds, state.urlScope.teamIds) ||
            !sameIds(state.selectedPromotionIds, state.urlScope.promotionIds) ||
            !state.showSlow ||
            !state.showFast ||
            !state.showUltraFast
          );
        },

        // Get API filter parameters
        getApiFilters: (): ChargePointFilterParams => {
          const state = get();
          const filters: ChargePointFilterParams = {};

          // Availability filters
          // When showOnlyAvailable is TRUE: send only_available=1 to API
          // When showOnlyAvailable is FALSE (default): send passive=1, busy_all=1, busy_queue=1 to show all
          if (state.showOnlyAvailable) {
            filters.only_available = '1';
          } else {
            filters.busy_all = '1';
            filters.busy_queue = '1';
            filters.passive = '1';
          }

          if (state.showAutoCharge) {
            filters.auto_charge = '1';
          }

          // Price filter
          if (state.maxPricePerKwh > 0) {
            filters.max_price = state.maxPricePerKwh;
          }

          // Charge speed filters
          const speeds = [];
          if (state.showSlow) speeds.push('slow');
          if (state.showFast) speeds.push('fast');
          if (state.showUltraFast) speeds.push('ultraFast');

          // Calculate min/max kW based on selected speeds
          if (speeds.length > 0 && speeds.length < 3) {
            const minKws: number[] = [];
            const maxKws: number[] = [];

            if (state.showSlow) {
              minKws.push(0);
              maxKws.push(SLOW_MAX_KW);
            }
            if (state.showFast) {
              minKws.push(FAST_MIN_KW);
              maxKws.push(FAST_MAX_KW);
            }
            if (state.showUltraFast) {
              minKws.push(ULTRA_FAST_MIN_KW);
              maxKws.push(999);
            }

            if (minKws.length > 0) {
              filters.min_kw = Math.min(...minKws);
              filters.max_kw = Math.max(...maxKws);
            }
          }

          // Operator filters - use team_ids and operator_ids from selected suggestions
          if (state.selectedTeamIds.length > 0) {
            filters.team_ids = state.selectedTeamIds.join(',');
          }
          if (state.selectedOperatorIds.length > 0) {
            filters.operator_ids = state.selectedOperatorIds.join(',');
          }

          // Promotion filters
          if (state.selectedPromotionIds.length > 0) {
            filters.promotion_ids = state.selectedPromotionIds.join(',');
          }

          return filters;
        },

        // Set "Only available" filter
        setShowOnlyAvailable: (value: boolean) => {
          set({ showOnlyAvailable: value });
        },

        setShowAutoCharge: (value: boolean) => {
          set({ showAutoCharge: value });
        },

        // Toggle charge speed filter
        toggleChargeSpeed: (speed: ChargeSpeedType) => {
          set((state) => {
            switch (speed) {
              case 'slow':
                state.showSlow = !state.showSlow;
                break;
              case 'fast':
                state.showFast = !state.showFast;
                break;
              case 'ultraFast':
                state.showUltraFast = !state.showUltraFast;
                break;
            }
          });
        },

        // Toggle operator (deprecated)
        toggleOperator: (operatorId: number) => {
          set((state) => {
            const index = state.selectedOperators.indexOf(operatorId);
            if (index > -1) {
              state.selectedOperators.splice(index, 1);
            } else {
              state.selectedOperators.push(operatorId);
            }
          });
        },

        // Toggle operator suggestion
        toggleOperatorSuggestion: (operator: {
          id: number;
          teamIds: number[];
          operatorIds: number[];
        }) => {
          set((state) => {
            const suggestionIndex = state.selectedOperatorSuggestionIds.indexOf(
              operator.id
            );

            if (suggestionIndex > -1) {
              // Remove this operator suggestion
              // Create new arrays instead of mutating to avoid readonly issues
              state.selectedOperatorSuggestionIds =
                state.selectedOperatorSuggestionIds.filter(
                  (id) => id !== operator.id
                );

              // Remove its team IDs and operator IDs
              operator.teamIds.forEach((teamId) => {
                state.selectedTeamIds = state.selectedTeamIds.filter(
                  (id) => id !== teamId
                );
              });
              operator.operatorIds.forEach((opId) => {
                state.selectedOperatorIds = state.selectedOperatorIds.filter(
                  (id) => id !== opId
                );
              });
            } else {
              // Add this operator suggestion
              // Create new arrays instead of mutating to avoid readonly issues
              state.selectedOperatorSuggestionIds = [
                ...state.selectedOperatorSuggestionIds,
                operator.id,
              ];

              // Add its team IDs and operator IDs
              operator.teamIds.forEach((teamId) => {
                if (!state.selectedTeamIds.includes(teamId)) {
                  state.selectedTeamIds = [...state.selectedTeamIds, teamId];
                }
              });
              operator.operatorIds.forEach((opId) => {
                if (!state.selectedOperatorIds.includes(opId)) {
                  state.selectedOperatorIds = [
                    ...state.selectedOperatorIds,
                    opId,
                  ];
                }
              });
            }
          });
        },

        // Set operator IDs
        // Note: This is deprecated in favor of applying selections in FilterPanel
        // after operators are loaded from the API
        setOperatorIds: (ids: number[]) => {
          set({
            selectedOperatorIds: ids,
            selectedOperatorSuggestionIds: ids,
          });
        },

        // Batch set operator suggestions (for URL deep linking).
        // Sets the operator/suggestion selection in one update.
        //
        // Team ids already in the store are PRESERVED (unioned), not replaced.
        // `operatorIds` and `teamIds` are independent URL filters: useUrlParams
        // applies a `?teamIds=` filter directly, then FilterPanel resolves
        // `?operatorIds=` to a matching suggestion and calls this to tick the
        // operator checkbox. A plain operator suggestion (e.g. Ewii) carries no
        // team_ids of its own, so REPLACING would silently drop a URL team
        // filter — `?operatorIds=742&teamIds=2047124` would then show the
        // operator's ENTIRE charge-point list instead of just that team's.
        setOperatorSuggestions: (
          operators: Array<{
            id: number;
            teamIds: number[];
            operatorIds: number[];
          }>
        ) => {
          set((state) => {
            const suggestionIds: number[] = [];
            // Seed with team ids already selected (e.g. a URL ?teamIds= filter)
            // so operator reconciliation never drops them.
            const teamIds: number[] = [...state.selectedTeamIds];
            const operatorIds: number[] = [];

            operators.forEach((op) => {
              suggestionIds.push(op.id);
              op.teamIds?.forEach((teamId) => {
                if (!teamIds.includes(teamId)) {
                  teamIds.push(teamId);
                }
              });
              op.operatorIds?.forEach((opId) => {
                if (!operatorIds.includes(opId)) {
                  operatorIds.push(opId);
                }
              });
            });

            state.selectedOperatorSuggestionIds = suggestionIds;
            state.selectedTeamIds = teamIds;
            // operator_ids is intentionally REPLACED (not unioned): a URL entry
            // may be a location-dependent suggestion id that must normalize to
            // the suggestion's real operator id(s). team_ids never carry that
            // ambiguity, hence the difference above.
            state.selectedOperatorIds = operatorIds;
          });
        },

        // Set max price
        setMaxPrice: (price: number) => {
          set({ maxPricePerKwh: Math.max(0, price) });
        },

        // Set min stability
        setMinStability: (score: number) => {
          set({ minStabilityScore: Math.max(0, Math.min(100, score)) });
        },

        // Reset all filters.
        // Clears what the visitor picked, then restores the scope the host page
        // pinned in the embed URL (empty on the standalone map).
        resetFilters: () => {
          const { urlScope } = get();
          set({
            showOnlyAvailable: false, // Default: show all charge points
            showAutoCharge: false,
            showSlow: true,
            showFast: true,
            showUltraFast: true,
            selectedOperators: [],
            selectedOperatorSuggestionIds: [...urlScope.operatorSuggestionIds],
            selectedTeamIds: [...urlScope.teamIds],
            selectedOperatorIds: [...urlScope.operatorIds],
            selectedPromotionIds: [...urlScope.promotionIds],
            maxPricePerKwh: 0,
            minStabilityScore: 0,
          });
        },

        // Pin the scope the embed URL asked for, so resetFilters returns to it.
        // Merges, so useUrlParams (raw params) and FilterPanel (ids resolved
        // from the operator suggestions) can each contribute their part.
        setUrlScope: (scope) => {
          set((state) => {
            state.urlScope = { ...state.urlScope, ...scope };
          });
        },

        // Toggle filter panel
        toggleFilterPanel: () => {
          set((state) => {
            state.isFilterPanelOpen = !state.isFilterPanelOpen;
          });
        },

        // Toggle best price modal
        toggleBestPriceModal: () => {
          set((state) => {
            state.isBestPriceModalOpen = !state.isBestPriceModalOpen;
          });
        },

        // Set best price modal open state
        setBestPriceModalOpen: (open: boolean) => {
          set({ isBestPriceModalOpen: open });
        },

        // Toggle section
        toggleSection: (section: keyof FilterState['expandedSections']) => {
          set((state) => {
            state.expandedSections[section] = !state.expandedSections[section];
          });
        },

        // Set controls enabled
        setControlsEnabled: (enabled: boolean) => {
          set({ controlsEnabled: enabled });
        },

        // Set location expanded
        setLocationExpanded: (expanded: boolean) => {
          set({ isLocationExpanded: expanded });
        },

        // Set show anchor point
        setShowAnchorPoint: (show: boolean) => {
          set({ showAnchorPoint: show });
        },

        // Set show location details
        setShowLocationDetails: (show: boolean) => {
          set({ showLocationDetails: show });
        },

        // Set show price filter section
        setShowPriceFilter: (show: boolean) => {
          set({ showPriceFilter: show });
        },

        // Set show Share button
        setShowShareButton: (show: boolean) => {
          set({ showShareButton: show });
        },

        // Set anchor point visibility (user's explicit choice)
        setAnchorPointVisible: (visible: boolean) => {
          set({ anchorPointVisible: visible });
        },

        // Set marker style
        setMarkerStyle: (
          style: 'default' | 'operator' | 'operator_neutral'
        ) => {
          set({ markerStyle: style });
        },
      })),
      {
        name: 'monta-filters',
        partialize: (state) => ({
          showOnlyAvailable: state.showOnlyAvailable,
          showAutoCharge: state.showAutoCharge,
          showSlow: state.showSlow,
          showFast: state.showFast,
          showUltraFast: state.showUltraFast,
          selectedOperators: state.selectedOperators,
          maxPricePerKwh: state.maxPricePerKwh,
        }),
      }
    ),
    { name: 'FilterStore' }
  )
);
